import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { format } from 'date-fns'
import {
  ArrowLeft, Save, Plus, Trash2,
  User, Calendar, MapPin, Users, Package,
  FileText, DollarSign, AlertTriangle, X, Search,
  Briefcase, CheckCircle, Mail, Phone, UserCheck,
  Send, Loader,
} from 'lucide-react'
import { buildJobIcs, downloadIcs, sanitizeIcsFilename } from '../lib/ics'
import {
  sendAvailabilityPing,
  sendCrewConfirmation,
} from '../lib/emailService'

// ─── Constants ───────────────────────────────────────────────────────────────

const EVENT_TYPES = ['wedding', 'corporate', 'birthday', 'festival', 'community', 'other']
const JOB_STATUSES = ['confirmed', 'staffing', 'crew_set', 'in_progress', 'completed', 'cancelled']
const SUCCESS_FEEDBACK_MS = 5000
const ERROR_FEEDBACK_MS = 10000

const STATUS_LABELS = {
  confirmed: 'Confirmed', staffing: 'Staffing', crew_set: 'Crew Set',
  in_progress: 'In Progress', completed: 'Completed', cancelled: 'Cancelled',
}

function fmtMoney(n) {
  return parseFloat(n || 0).toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function today() {
  return format(new Date(), 'yyyy-MM-dd')
}

// ─── Client Search (same pattern as QuoteDetailPage) ─────────────────────────

function ClientSearch({ selectedClient, onSelect }) {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState([])
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    function handleOut(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false)
    }
    document.addEventListener('mousedown', handleOut)
    return () => document.removeEventListener('mousedown', handleOut)
  }, [])

  useEffect(() => {
    if (!query.trim()) { setResults([]); return }
    const t = setTimeout(async () => {
      const { data } = await supabase
        .from('clients')
        .select('id, name, email, phone')
        .or(`name.ilike.%${query}%,email.ilike.%${query}%,phone.ilike.%${query}%`)
        .limit(8)
      setResults(data || [])
      setOpen(true)
    }, 250)
    return () => clearTimeout(t)
  }, [query])

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {selectedClient ? (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 12px', border: '1px solid var(--color-border-strong)',
          borderRadius: 'var(--radius-sm)', background: 'white',
        }}>
          <div>
            <span style={{ fontWeight: 600 }}>{selectedClient.name}</span>
            {selectedClient.email && (
              <span style={{ marginLeft: 8, fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                {selectedClient.email}
              </span>
            )}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => onSelect(null)}><X size={14} /></button>
        </div>
      ) : (
        <div style={{ position: 'relative' }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }} />
          <input className="form-input" style={{ paddingLeft: 32 }}
            placeholder="Search client by name, email, or phone…"
            value={query} onChange={e => setQuery(e.target.value)}
            onFocus={() => query && setOpen(true)} />
        </div>
      )}
      {open && results.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200,
          background: 'white', border: '1px solid var(--color-border-strong)',
          borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)',
          marginTop: 4, overflow: 'hidden',
        }}>
          {results.map(c => (
            <button key={c.id} onMouseDown={() => { onSelect(c); setQuery(''); setOpen(false) }}
              style={{
                display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px',
                border: 'none', background: 'none', cursor: 'pointer',
                fontFamily: 'var(--font-body)', borderBottom: '1px solid var(--color-border)',
              }}
              onMouseOver={e => e.currentTarget.style.background = 'var(--color-bg)'}
              onMouseOut={e => e.currentTarget.style.background = 'none'}
            >
              <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>{c.name}</div>
              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                {[c.email, c.phone].filter(Boolean).join(' · ')}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Inventory Category Picker ────────────────────────────────────────────────

function CategoryPicker({ onSelect, onClose, excludeIds = [] }) {
  const [cats, setCats] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let mounted = true

    supabase.from('inventory_categories').select('*').eq('active', true).order('name')
      .then(({ data }) => {
        if (!mounted) return
        setCats(data || [])
      })
      .catch(err => {
        console.error('Failed to load inventory categories:', err)
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })

    return () => { mounted = false }
  }, [])

  const available = cats.filter(c => !excludeIds.includes(c.id))

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 420 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 700 }}>Add Inventory Item</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose}><X size={16} /></button>
        </div>
        <div style={{ maxHeight: 360, overflowY: 'auto', padding: '8px 0' }}>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-muted)' }}>Loading…</div>
          ) : available.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--color-text-muted)' }}>All categories already added.</div>
          ) : available.map(cat => (
            <button key={cat.id} onClick={() => { onSelect(cat); onClose() }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                width: '100%', textAlign: 'left', padding: '12px 20px',
                border: 'none', background: 'none', cursor: 'pointer',
                fontFamily: 'var(--font-body)', borderBottom: '1px solid var(--color-border)',
              }}
              onMouseOver={e => e.currentTarget.style.background = 'var(--color-bg)'}
              onMouseOut={e => e.currentTarget.style.background = 'none'}
            >
              <div>
                <div style={{ fontWeight: 500, fontSize: 'var(--text-sm)' }}>{cat.name}</div>
                <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                  {cat.total_quantity} {cat.unit}s total
                </div>
              </div>
              <Plus size={14} style={{ color: 'var(--color-text-muted)' }} />
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Staff Picker Modal ───────────────────────────────────────────────────────
// Lets admin search and add a labor user to a job's staffing list.

function StaffPicker({ onSelect, onClose, excludeIds = [] }) {
  const [staff, setStaff] = useState([])
  const [loading, setLoading] = useState(true)
  const [addingId, setAddingId] = useState(null)

  useEffect(() => {
    let mounted = true

    supabase.from('users')
      .select('id, full_name, email, phone, role, active')
      .eq('active', true)
      .order('full_name')
      .then(({ data }) => {
        if (!mounted) return
        setStaff(data || [])
      })
      .catch(err => {
        console.error('Failed to load staff list:', err)
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })

    return () => { mounted = false }
  }, [])

  const available = staff.filter(u => !excludeIds.includes(u.id))

  async function handleAddClick(u) {
    if (addingId) return
    setAddingId(u.id)
    try {
      const ok = await onSelect(u)
      if (ok !== false) onClose()
    } finally {
      setAddingId(null)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" data-testid="crew-picker-modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 700 }}>Add Crew Member</h3>
          <button className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close crew picker"><X size={16} /></button>
        </div>
        <div style={{ padding: '8px 0 12px' }}>
          {loading ? (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--color-text-muted)' }}>Loading staff…</div>
          ) : available.length === 0 ? (
            <div style={{ padding: '24px', textAlign: 'center', color: 'var(--color-text-muted)' }}>
              All active staff members are already on this job.
            </div>
          ) : (
            available.map(u => (
              <div
                key={u.id}
                data-testid={`staff-row-${u.id}`}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                  padding: '10px 20px', borderBottom: '1px solid var(--color-border)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                    background: 'var(--color-navy)', color: 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 700, fontSize: 13,
                  }}>
                    {(u.full_name || 'U').charAt(0).toUpperCase()}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>{u.full_name}</div>
                    <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                      {[u.email, u.phone].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  data-testid={`add-staff-button-${u.id}`}
                  aria-label={`Add ${u.full_name || 'staff member'} to job`}
                  disabled={!!addingId}
                  onClick={() => handleAddClick(u)}
                  style={{ minWidth: 86, flexShrink: 0 }}
                >
                  {addingId === u.id ? 'Adding…' : 'Add'}
                </button>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function JobDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const jobId = typeof id === 'string' && id.trim() ? id : null
  const isNew = !jobId || jobId === 'new'

  // Form state
  const [client, setClient] = useState(null)
  const [eventDate, setEventDate] = useState('')
  const [setupDate, setSetupDate] = useState('')
  const [teardownDate, setTeardownDate] = useState('')
  const [eventType, setEventType] = useState('')
  const [address, setAddress] = useState('')
  const [status, setStatus] = useState('confirmed')
  const [crewRequired, setCrewRequired] = useState(2)
  const [setupHours, setSetupHours] = useState('')
  const [teardownHours, setTeardownHours] = useState('')
  const [depositRequired, setDepositRequired] = useState('')
  const [depositReceived, setDepositReceived] = useState('')
  const [depositReceivedDate, setDepositReceivedDate] = useState('')
  const [paymentStatus, setPaymentStatus] = useState('pending')
  const [adminNotes, setAdminNotes] = useState('')
  const [jobNotes, setJobNotes] = useState('')
  const [linkedQuote, setLinkedQuote] = useState(null)
  const [inventoryItems, setInventoryItems] = useState([]) // [{category, quantity}]
  const [availability, setAvailability] = useState({}) // {category_id: available_qty}

  // UI state
  const [job, setJob] = useState(null)
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [calendarMsg, setCalendarMsg] = useState({ type: '', text: '' })
  const [showCatPicker, setShowCatPicker] = useState(false)
  const [conflictJobs, setConflictJobs] = useState([])
  const [inventoryConflicts, setInventoryConflicts] = useState([]) // category names over-reserved

  // Tracks the last status value persisted to the DB.
  // Guards the auto-save effect against load-triggered and no-op status changes.
  const lastSavedStatusRef = useRef(null)

  useEffect(() => {
    const msg = location.state?.flashSuccess
    if (!msg) return
    setSuccess(msg)
    setTimeout(() => setSuccess(''), SUCCESS_FEEDBACK_MS)
    navigate(location.pathname, { replace: true, state: null })
  }, [location.pathname, location.state, navigate])

  // Staffing state
  const [staffAssignments, setStaffAssignments]   = useState([]) // [{job_staff row + user}]
  const [showStaffPicker, setShowStaffPicker]     = useState(false)
  const [pingingSend, setPingingSend]             = useState({}) // {staffId: true} while sending
  const [staffMsg, setStaffMsg]                   = useState('')  // inline feedback msg

  function showStaffFeedback(message, { isError = false } = {}) {
    setStaffMsg(message)
    setTimeout(() => setStaffMsg(''), isError ? ERROR_FEEDBACK_MS : SUCCESS_FEEDBACK_MS)
  }

  // Load existing job
  useEffect(() => {
    if (isNew) return
    async function load() {
      setLoading(true)
      const { data: j, error } = await supabase
        .from('jobs')
        .select('*, clients(*), quotes(quote_number, grand_total), job_inventory(*, inventory_categories(name, total_quantity, unit))')
        .eq('id', jobId)
        .single()

      if (error || !j) { navigate('/jobs'); return }

      setJob(j)
      setClient(j.clients)
      setEventDate(j.event_date || '')
      setSetupDate(j.setup_date || '')
      setTeardownDate(j.teardown_date || '')
      setEventType(j.event_type || '')
      setAddress(j.address || '')
      setStatus(j.status || 'confirmed')
      lastSavedStatusRef.current = j.status || 'confirmed'
      setCrewRequired(j.crew_required ?? 2)
      setSetupHours(j.estimated_setup_hours || '')
      setTeardownHours(j.estimated_teardown_hours || '')
      setDepositRequired(j.deposit_required || '')
      setDepositReceived(j.deposit_received || '')
      setDepositReceivedDate(j.deposit_received_date || '')
      setPaymentStatus(j.payment_status || 'pending')
      setAdminNotes(j.admin_notes || '')
      setJobNotes(j.job_notes || '')
      setLinkedQuote(j.quotes || null)
      setInventoryItems((j.job_inventory || []).map(ji => ({
        _key: ji.id,
        category_id: ji.inventory_category_id,
        category_name: ji.inventory_categories?.name || '—',
        total_quantity: ji.inventory_categories?.total_quantity || 0,
        unit: ji.inventory_categories?.unit || 'unit',
        quantity: ji.quantity_reserved,
      })))
      // Load staff assignments
      await loadStaff(j.id)

      setLoading(false)
    }
    load()
  }, [id, isNew, navigate]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save status changes immediately when the operator picks a new status.
  // Guards:
  //   - skip new jobs (no DB row yet)
  //   - skip if status matches lastSavedStatusRef — prevents firing on initial
  //     mount and on the load-triggered setStatus call
  useEffect(() => {
    if (isNew || !jobId) return
    if (status === lastSavedStatusRef.current) return
    async function autoSaveStatus() {
      const { error: err } = await supabase
        .from('jobs')
        .update({ status })
        .eq('id', jobId)
      if (err) {
        setError('Status update failed: ' + err.message)
      } else {
        lastSavedStatusRef.current = status
        setSuccess('Status saved.')
        setTimeout(() => setSuccess(''), SUCCESS_FEEDBACK_MS)
      }
    }
    autoSaveStatus()
  }, [status, isNew, jobId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Load staff for this job (can be called independently to refresh)
  async function loadStaff(jobId) {
    const jid = jobId || job?.id
    if (!jid) return
    const { data } = await supabase
      .from('job_staff')
      .select('*, users(id, full_name, email, phone)')
      .eq('job_id', jid)
      .order('id')
    setStaffAssignments(data || [])
  }

  // Check date conflicts: other active jobs overlapping these dates
  const checkDateConflicts = useCallback(async (start, end) => {
    if (!start) return
    const checkEnd = end || start
    const { data } = await supabase
      .from('jobs')
      .select('id, event_date, clients(name), event_type')
      .not('status', 'in', '("cancelled","completed")')
      .lte('setup_date', checkEnd)
      .gte('teardown_date', start)
      .neq('id', isNew ? '00000000-0000-0000-0000-000000000000' : jobId)
    setConflictJobs(data || [])
  }, [id, isNew])

  // Check inventory availability for current dates
  const checkInventoryAvailability = useCallback(async (start, end) => {
    if (!start || inventoryItems.length === 0) { setAvailability({}); return }
    const checkEnd = end || start
    const { data } = await supabase.rpc('get_inventory_availability', {
      check_start: start,
      check_end: checkEnd,
    })
    if (data) {
      const map = {}
      data.forEach(row => { map[row.category_id] = row.available_quantity })
      setAvailability(map)

      // Check for conflicts
      const conflicts = inventoryItems.filter(item => {
        const avail = map[item.category_id]
        if (avail === undefined) return false
        // Available includes this job's current reservation — add it back
        return item.quantity > avail + (isNew ? 0 : item.quantity)
      }).map(item => item.category_name)
      setInventoryConflicts(conflicts)
    }
  }, [inventoryItems, isNew])

  // Re-check when dates change
  useEffect(() => {
    const start = setupDate || eventDate
    const end = teardownDate || eventDate
    if (start) {
      checkDateConflicts(start, end)
      checkInventoryAvailability(start, end)
    }
  }, [setupDate, eventDate, teardownDate, checkDateConflicts, checkInventoryAvailability])

  function addInventoryItem(cat) {
    setInventoryItems(prev => [...prev, {
      _key: Date.now(),
      category_id: cat.id,
      category_name: cat.name,
      total_quantity: cat.total_quantity,
      unit: cat.unit,
      quantity: 1,
    }])
  }

  function updateInventoryQty(index, qty) {
    setInventoryItems(prev => prev.map((item, i) => i === index ? { ...item, quantity: qty } : item))
  }

  function removeInventoryItem(index) {
    setInventoryItems(prev => prev.filter((_, i) => i !== index))
  }

  // ─── Staffing handlers ────────────────────────────────────────────────────

  async function handleAddStaff(user) {
    if (!job?.id) {
      showStaffFeedback('Please save the job first before adding crew.', { isError: true })
      return false
    }
    const { error: insErr } = await supabase.from('job_staff').insert({
      job_id: job.id,
      user_id: user.id,
      status: 'pinged',
      pinged_at: null, // will be set when ping is sent
    })
    if (insErr && insErr.code !== '23505') {
      showStaffFeedback(`Error: ${insErr.message}`, { isError: true })
      return false
    }

    await loadStaff()
    if (insErr?.code === '23505') {
      showStaffFeedback(`${user.full_name || 'Staff member'} is already assigned to this job.`)
    } else {
      showStaffFeedback(`${user.full_name || 'Staff member'} added to crew.`)
    }
    return true
  }

  async function handleRemoveStaff(assignmentId) {
    await supabase.from('job_staff').delete().eq('id', assignmentId)
    await loadStaff()
  }

  async function handleSendPing(assignment) {
    if (!job) return
    setPingingSend(prev => ({ ...prev, [assignment.id]: true }))
    setStaffMsg('')

    // Build the job data object for the email
    const jobData = {
      id: job.id,
      event_date: eventDate,
      setup_date: setupDate,
      teardown_date: teardownDate,
      event_type: eventType,
      address,
      crew_required: crewRequired,
      estimated_setup_hours: setupHours,
      estimated_teardown_hours: teardownHours,
    }

    const staffMember = {
      id: assignment.user_id,
      full_name: assignment.users?.full_name || 'Staff',
      email: assignment.users?.email || '',
      response_token: assignment.response_token,
    }

    // Update pinged_at first
    await supabase.from('job_staff')
      .update({ status: 'pinged', pinged_at: new Date().toISOString() })
      .eq('id', assignment.id)

    const result = await sendAvailabilityPing(staffMember, jobData)

    if (result.ok) {
      showStaffFeedback(`Ping sent to ${staffMember.full_name}!`)
    } else {
      showStaffFeedback(`Could not send ping: ${result.error}`, { isError: true })
    }
    setPingingSend(prev => ({ ...prev, [assignment.id]: false }))
    await loadStaff()
  }

  async function handleSendAllPings() {
    if (!job) return
    const toPing = staffAssignments.filter(a => a.status === 'pinged' && !a.pinged_at)
    if (toPing.length === 0) {
      showStaffFeedback('All staff members have already been pinged.')
      return
    }
    for (const a of toPing) {
      await handleSendPing(a)
    }
  }

  async function handleConfirmStaff(assignment) {
    if (!job) return
    setPingingSend(prev => ({ ...prev, [assignment.id]: true }))
    await supabase.from('job_staff')
      .update({ status: 'confirmed', confirmed_at: new Date().toISOString() })
      .eq('id', assignment.id)

    const jobData = {
      id: job.id,
      event_date: eventDate,
      setup_date: setupDate,
      teardown_date: teardownDate,
      event_type: eventType,
      address,
      crew_required: crewRequired,
      estimated_setup_hours: setupHours,
      estimated_teardown_hours: teardownHours,
      job_notes: jobNotes,
    }
    const staffMember = {
      full_name: assignment.users?.full_name || 'Staff',
      email: assignment.users?.email || '',
    }
    const result = await sendCrewConfirmation(staffMember, jobData)
    if (result.ok) {
      showStaffFeedback(`${staffMember.full_name} confirmed and notified.`)
    } else {
      showStaffFeedback(`Confirmed locally; email failed: ${result.error}`, { isError: true })
    }
    setPingingSend(prev => ({ ...prev, [assignment.id]: false }))
    await loadStaff()
  }

  function handleAddToCalendar() {
    if (!eventDate) {
      setCalendarMsg({ type: 'error', text: 'Add an event date before exporting to calendar.' })
      return
    }

    try {
      const clientName = client?.name || 'Client'
      const summary = [eventType || 'Event', clientName].filter(Boolean).join(' - ')
      const uidBase = job?.id || 'new-job'
      const uid = `wtr-job-${uidBase}@wtr-crm`
      const description = [
        job?.id ? `Job ID: ${job.id}` : null,
        adminNotes ? `Admin notes: ${adminNotes}` : null,
        jobNotes ? `Job notes: ${jobNotes}` : null,
      ].filter(Boolean).join('\n')

      const icsText = buildJobIcs({
        uid,
        summary,
        startDate: eventDate,
        endDate: teardownDate || eventDate,
        location: address || '',
        description,
      })

      const filenameBase = [
        sanitizeIcsFilename(client?.name || ''),
        sanitizeIcsFilename(eventDate),
      ].filter(Boolean).join('-') || `job-${uidBase}`

      downloadIcs(icsText, `${filenameBase}.ics`)
      setCalendarMsg({ type: 'success', text: 'Calendar file downloaded.' })
      setTimeout(() => setCalendarMsg({ type: '', text: '' }), SUCCESS_FEEDBACK_MS)
    } catch (err) {
      console.error('ICS export failed:', err)
      setCalendarMsg({ type: 'error', text: 'Could not generate calendar file.' })
    }
  }

  async function handleSave() {
    if (!client) return setError('Please select a client.')
    if (!eventDate) return setError('Event date is required.')
    setSaving(true)
    setError('')
    setSuccess('')

    const payload = {
      client_id: client.id,
      event_date: eventDate,
      setup_date: setupDate || eventDate,
      teardown_date: teardownDate || eventDate,
      event_type: eventType || null,
      address: address || null,
      status,
      crew_required: parseInt(crewRequired) || 0,
      estimated_setup_hours: parseFloat(setupHours) || 0,
      estimated_teardown_hours: parseFloat(teardownHours) || 0,
      deposit_required: parseFloat(depositRequired) || 0,
      deposit_received: parseFloat(depositReceived) || 0,
      deposit_received_date: depositReceivedDate || null,
      payment_status: paymentStatus,
      admin_notes: adminNotes || null,
      job_notes: jobNotes || null,
    }

    let targetJobId = isNew ? null : jobId

    if (isNew) {
      const fromQuoteId = searchParams.get('from_quote')
      if (fromQuoteId) payload.quote_id = fromQuoteId

      const { data: newJob, error: jErr } = await supabase
        .from('jobs').insert(payload).select().single()
      if (jErr) { setSaving(false); return setError(jErr.message) }
      targetJobId = newJob.id
      setJob(newJob)
    } else {
      const { error: jErr } = await supabase
        .from('jobs').update(payload).eq('id', targetJobId)
      if (jErr) { setSaving(false); return setError(jErr.message) }
    }

    // Upsert inventory: delete all, insert current
    await supabase.from('job_inventory').delete().eq('job_id', targetJobId)
    if (inventoryItems.length > 0) {
      const { error: invErr } = await supabase.from('job_inventory').insert(
        inventoryItems.map(item => ({
          job_id: targetJobId,
          inventory_category_id: item.category_id,
          quantity_reserved: parseInt(item.quantity) || 0,
        }))
      )
      if (invErr) { setSaving(false); return setError(invErr.message) }
    }

    setSaving(false)
    setSuccess('Job saved.')
    if (isNew) navigate(`/jobs/${targetJobId}`, { replace: true })
    setTimeout(() => setSuccess(''), SUCCESS_FEEDBACK_MS)
  }

  if (loading) return <div style={{ padding: 32, color: 'var(--color-text-muted)' }}>Loading job…</div>

  const balanceDue = Math.max(0, (parseFloat(depositRequired) || 0) - (parseFloat(depositReceived) || 0))
  const existingCatIds = inventoryItems.map(i => i.category_id)

  // ── Next Step panel config ────────────────────────────────────────────────
  // Derives the most important next action from the current job state.
  // Uses only data already loaded on this page — no extra fetches.
  const crewNeeded    = parseInt(crewRequired) || 0
  const confirmedCrew = staffAssignments.filter(a => a.status === 'confirmed').length
  const assignedCrew  = staffAssignments.length

  const nextStep = (() => {
    if (isNew)
      return { bg: '#f8fafc', border: '#e2e8f0', color: '#475569',
               msg: 'Save this job to continue.' }

    if (status === 'confirmed')
      return { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af',
               msg: 'Next step: assign crew for this booking. Use the Crew panel below.' }

    if (status === 'staffing') {
      if (assignedCrew === 0)
        return { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af',
                 msg: 'No crew assigned yet. Add crew members and send availability pings.' }
      if (confirmedCrew === 0)
        return { bg: '#fefce8', border: '#fde68a', color: '#92400e',
                 msg: `${assignedCrew} crew pinged, none confirmed yet. Follow up or add more staff.` }
      return { bg: '#fefce8', border: '#fde68a', color: '#92400e',
               msg: `${confirmedCrew} of ${crewNeeded} crew confirmed. Continue until the full crew is set.` }
    }

    if (status === 'crew_set')
      return { bg: '#f0fdf4', border: '#bbf7d0', color: '#166534',
               msg: `Crew is set (${confirmedCrew}/${crewNeeded} confirmed). Start the job when work begins.`,
               action: 'start' }

    if (status === 'in_progress')
      return { bg: '#eff6ff', border: '#bfdbfe', color: '#1e40af',
               msg: 'Job is in progress. Mark it complete when the event is finished.',
               action: 'complete' }

    if (status === 'completed')
      return { bg: '#f0fdf4', border: '#bbf7d0', color: '#166534',
               msg: 'This job is complete. No further action required.' }

    if (status === 'cancelled')
      return { bg: '#fafafa', border: '#e5e7eb', color: '#6b7280',
               msg: 'This job is cancelled. Change the status above to reopen it if needed.' }

    return null
  })()
  const checkStart = setupDate || eventDate
  const checkEnd = teardownDate || eventDate

  return (
    <div data-testid="job-detail-page">
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 'var(--sp-5)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate('/jobs')}><ArrowLeft size={16} /></button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <h1 className="page-title" style={{ fontSize: 'var(--text-xl)' }}>
                {isNew ? 'New Job' : (client?.name ? `${client.name} — ${eventDate ? format(new Date(eventDate + 'T12:00:00'), 'MMM d, yyyy') : 'Job'}` : 'Job')}
              </h1>
              {!isNew && (
                <span className={`badge badge-${status}`}>{STATUS_LABELS[status]}</span>
              )}
            </div>
            {linkedQuote && (
              <p className="page-subtitle">
                From quote{' '}
                <a href={`/quotes/${job?.quote_id}`} onClick={e => { e.preventDefault(); navigate(`/quotes/${job?.quote_id}`) }}>
                  {linkedQuote.quote_number}
                </a>
                {linkedQuote.grand_total && ` · $${fmtMoney(linkedQuote.grand_total)}`}
              </p>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            data-testid={eventDate ? 'job-add-to-calendar-button' : 'job-add-to-calendar-disabled'}
            className="btn btn-ghost"
            onClick={handleAddToCalendar}
            disabled={!eventDate}
            title={!eventDate ? 'Add an event date before exporting to calendar' : 'Download .ics calendar file'}
          >
            <Calendar size={14} /> Add to Calendar
          </button>
          <button data-testid="job-save-button" className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <Save size={14} /> {saving ? 'Saving…' : 'Save Job'}
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error" data-testid="feedback-banner-error" style={{ marginBottom: 'var(--sp-4)' }}>{error}</div>}
      {success && <div className="alert alert-success" data-testid="feedback-banner-success" style={{ marginBottom: 'var(--sp-4)' }}>{success}</div>}
      {calendarMsg.type === 'error' && (
        <div className="alert alert-error" data-testid="job-add-to-calendar-error" style={{ marginBottom: 'var(--sp-4)' }}>
          {calendarMsg.text}
        </div>
      )}
      {calendarMsg.type === 'success' && (
        <div className="alert alert-success" data-testid="job-add-to-calendar-success" style={{ marginBottom: 'var(--sp-4)' }}>
          {calendarMsg.text}
        </div>
      )}

      {!isNew && (
        <div className="card" data-testid="job-status-section" style={{ marginBottom: 'var(--sp-4)' }}>
          <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ fontWeight: 700 }}>Job Status</div>
            <span className={`badge badge-${status}`}>{STATUS_LABELS[status]}</span>
            <div className="form-group" style={{ margin: 0, minWidth: 220 }}>
              <label className="form-label" style={{ marginBottom: 4 }}>Update Status</label>
              <select data-testid="job-status-select" className="form-select" value={status}
                onChange={e => setStatus(e.target.value)}>
                {JOB_STATUSES.map(s => (
                  <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Date conflict warning */}
      {conflictJobs.length > 0 && (
        <div className="alert alert-info" style={{ marginBottom: 'var(--sp-4)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <strong>Scheduling note:</strong> {conflictJobs.length} other active job{conflictJobs.length > 1 ? 's' : ''} overlap{conflictJobs.length === 1 ? 's' : ''} these dates — review staffing availability before confirming crew.
            <div style={{ marginTop: 4, fontSize: 'var(--text-xs)', opacity: 0.8 }}>
              {conflictJobs.map(j => `${j.clients?.name || 'Unknown'} (${j.event_date})`).join(' · ')}
            </div>
          </div>
        </div>
      )}

      {/* Inventory conflict warning */}
      {inventoryConflicts.length > 0 && (
        <div className="alert alert-error" style={{ marginBottom: 'var(--sp-4)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <strong>Inventory conflict:</strong> Quantity exceeds available stock for: {inventoryConflicts.join(', ')}.
            Check the Inventory page for availability on these dates.
          </div>
        </div>
      )}

      <div data-testid="jobs-section" style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 'var(--sp-5)', alignItems: 'start' }}>

        {/* ── Left column ─────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)' }}>

          {/* Client */}
          <div className="card">
            <div className="card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <User size={15} style={{ color: 'var(--color-text-muted)' }} />
                <span style={{ fontWeight: 700 }}>Client</span>
              </div>
            </div>
            <div className="card-body">
              <ClientSearch selectedClient={client} onSelect={setClient} />
            </div>
          </div>

          {/* Event Details */}
          <div className="card">
            <div className="card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Calendar size={15} style={{ color: 'var(--color-text-muted)' }} />
                <span style={{ fontWeight: 700 }}>Event Details</span>
              </div>
            </div>
            <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
              <div className="form-grid form-grid-3">
                <div className="form-group">
                  <label className="form-label">Setup Date</label>
                  <input className="form-input" type="date" value={setupDate}
                    onChange={e => setSetupDate(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Event Date *</label>
                  <input className="form-input" type="date" value={eventDate}
                    onChange={e => setEventDate(e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Teardown Date</label>
                  <input className="form-input" type="date" value={teardownDate}
                    onChange={e => setTeardownDate(e.target.value)} />
                </div>
              </div>
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label className="form-label">Event Type</label>
                  <select className="form-select" value={eventType}
                    onChange={e => setEventType(e.target.value)}>
                    <option value="">Select type…</option>
                    {EVENT_TYPES.map(t => (
                      <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Crew Required</label>
                  <input className="form-input" type="number" min="0" value={crewRequired}
                    onChange={e => setCrewRequired(e.target.value)} />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">
                  <MapPin size={12} style={{ display: 'inline', marginRight: 4 }} />
                  Delivery / Setup Address
                </label>
                <input className="form-input" value={address}
                  onChange={e => setAddress(e.target.value)}
                  placeholder="123 Main St, Winnipeg, MB" />
              </div>
            </div>
          </div>

          {/* Logistics */}
          <div className="card">
            <div className="card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Users size={15} style={{ color: 'var(--color-text-muted)' }} />
                <span style={{ fontWeight: 700 }}>Logistics</span>
              </div>
            </div>
            <div className="card-body">
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label className="form-label">Est. Setup Hours</label>
                  <input className="form-input" type="number" min="0" step="0.5" value={setupHours}
                    onChange={e => setSetupHours(e.target.value)} placeholder="e.g. 4" />
                </div>
                <div className="form-group">
                  <label className="form-label">Est. Teardown Hours</label>
                  <input className="form-input" type="number" min="0" step="0.5" value={teardownHours}
                    onChange={e => setTeardownHours(e.target.value)} placeholder="e.g. 2" />
                </div>
              </div>
              {(setupHours || teardownHours) && (
                <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--color-bg)', borderRadius: 'var(--radius-sm)', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                  Total crew-hours: <strong>{((parseFloat(setupHours) || 0) + (parseFloat(teardownHours) || 0)) * (parseInt(crewRequired) || 1)}</strong> hrs
                  ({crewRequired} crew × {(parseFloat(setupHours) || 0) + (parseFloat(teardownHours) || 0)} hrs)
                </div>
              )}
            </div>
          </div>

          {/* Inventory Allocation */}
          <div className="card">
            <div className="card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Package size={15} style={{ color: 'var(--color-text-muted)' }} />
                <span style={{ fontWeight: 700 }}>Inventory Allocation</span>
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => setShowCatPicker(true)}>
                <Plus size={13} /> Add Item
              </button>
            </div>

            {inventoryItems.length === 0 ? (
              <div className="empty-state" style={{ padding: 'var(--sp-8)' }}>
                <Package size={28} style={{ opacity: 0.25 }} />
                <p style={{ fontSize: 'var(--text-sm)' }}>No inventory reserved yet.</p>
                <button className="btn btn-primary btn-sm" onClick={() => setShowCatPicker(true)}>
                  <Plus size={13} /> Add Item
                </button>
              </div>
            ) : (
              <div>
                {/* Header */}
                <div style={{
                  display: 'grid', gridTemplateColumns: '1fr 100px 100px 36px',
                  gap: 8, padding: '8px 20px', borderBottom: '1px solid var(--color-border)',
                  fontSize: 'var(--text-xs)', fontWeight: 600, textTransform: 'uppercase',
                  letterSpacing: '0.05em', color: 'var(--color-text-muted)',
                }}>
                  <span>Category</span>
                  <span style={{ textAlign: 'center' }}>Reserved</span>
                  <span style={{ textAlign: 'center' }}>Available{checkStart ? '*' : ''}</span>
                  <span></span>
                </div>
                {inventoryItems.map((item, i) => {
                  const avail = availability[item.category_id]
                  const overLimit = avail !== undefined && item.quantity > avail
                  return (
                    <div key={item._key || item.category_id} style={{
                      display: 'grid', gridTemplateColumns: '1fr 100px 100px 36px',
                      gap: 8, padding: '10px 20px', borderBottom: '1px solid var(--color-border)',
                      alignItems: 'center',
                      background: overLimit ? '#fff5f5' : undefined,
                    }}>
                      <div>
                        <div style={{ fontWeight: 500, fontSize: 'var(--text-sm)' }}>{item.category_name}</div>
                        <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                          {item.total_quantity} {item.unit}s total
                        </div>
                      </div>
                      <input
                        className="form-input"
                        style={{ textAlign: 'center', fontSize: 'var(--text-sm)', borderColor: overLimit ? 'var(--color-red)' : undefined }}
                        type="number" min="0"
                        value={item.quantity}
                        onChange={e => updateInventoryQty(i, e.target.value)}
                      />
                      <div style={{ textAlign: 'center', fontSize: 'var(--text-sm)' }}>
                        {avail !== undefined ? (
                          <span style={{
                            fontWeight: 600,
                            color: overLimit ? 'var(--color-red)' : avail < item.total_quantity * 0.25 ? 'var(--color-amber)' : 'var(--color-green)',
                          }}>
                            {overLimit && '⚠ '}{avail}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--color-text-muted)', fontSize: 'var(--text-xs)' }}>
                            {checkStart ? '—' : 'set dates'}
                          </span>
                        )}
                      </div>
                      <button className="btn btn-ghost btn-sm" style={{ color: 'var(--color-red)', padding: 4 }}
                        onClick={() => removeInventoryItem(i)}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  )
                })}
                {checkStart && (
                  <div style={{ padding: '8px 20px', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                    * Availability checked for {checkStart}{checkEnd !== checkStart ? ` – ${checkEnd}` : ''}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Notes */}
          <div className="card">
            <div className="card-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileText size={15} style={{ color: 'var(--color-text-muted)' }} />
                <span style={{ fontWeight: 700 }}>Notes</span>
              </div>
            </div>
            <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}>
              <div className="form-group">
                <label className="form-label">Admin Notes <span style={{ fontWeight: 400, color: 'var(--color-text-muted)' }}>(internal only)</span></label>
                <textarea className="form-textarea" rows={3} value={adminNotes}
                  onChange={e => setAdminNotes(e.target.value)}
                  placeholder="Internal notes — not visible to staff…" />
              </div>
              <div className="form-group">
                <label className="form-label">Job Notes <span style={{ fontWeight: 400, color: 'var(--color-text-muted)' }}>(visible to assigned crew)</span></label>
                <textarea className="form-textarea" rows={3} value={jobNotes}
                  onChange={e => setJobNotes(e.target.value)}
                  placeholder="Site access instructions, parking, special requirements…" />
              </div>
            </div>
          </div>
        </div>

        {/* ── Right column ─────────────────────────────────────────────── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-5)', position: 'sticky', top: 24 }}>

          {/* Status */}
          <div className="card">
            <div className="card-header" style={{ padding: '14px 18px' }}>
              <span style={{ fontWeight: 700 }}>Job Status</span>
              <span className={`badge badge-${status}`}>{STATUS_LABELS[status]}</span>
            </div>
            <div className="card-body" style={{ padding: '16px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>Current status</span>
                <span className={`badge badge-${status}`}>{STATUS_LABELS[status]}</span>
              </div>
              {status === 'cancelled' && (
                <div className="alert alert-error" style={{ marginTop: 10, fontSize: 'var(--text-xs)' }}>
                  Cancelling will not automatically release inventory reservations. Remove inventory items manually if needed.
                </div>
              )}
              {!isNew && client?.id && (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => navigate(`/clients/${client.id}`)}
                  style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}
                >
                  <User size={13} /> View Client →
                </button>
              )}
            </div>
          </div>

          {/* Payment */}
          <div className="card">
            <div className="card-header" style={{ padding: '14px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <DollarSign size={15} style={{ color: 'var(--color-text-muted)' }} />
                <span style={{ fontWeight: 700 }}>Payment</span>
              </div>
            </div>
            <div className="card-body" style={{ padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
              <div className="form-group">
                <label className="form-label">Deposit Required ($)</label>
                <input className="form-input" type="number" min="0" step="0.01"
                  value={depositRequired} onChange={e => setDepositRequired(e.target.value)} placeholder="0.00" />
              </div>
              <div className="form-group">
                <label className="form-label">Deposit Received ($)</label>
                <input className="form-input" type="number" min="0" step="0.01"
                  value={depositReceived} onChange={e => setDepositReceived(e.target.value)} placeholder="0.00" />
              </div>
              <div className="form-group">
                <label className="form-label">Date Received</label>
                <input className="form-input" type="date"
                  value={depositReceivedDate} onChange={e => setDepositReceivedDate(e.target.value)} />
              </div>
              <div style={{
                background: balanceDue > 0 ? '#fef3c7' : '#d1fae5',
                borderRadius: 'var(--radius-sm)', padding: '10px 12px',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: balanceDue > 0 ? '#92400e' : '#065f46' }}>
                  Balance Due
                </span>
                <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: balanceDue > 0 ? '#92400e' : '#065f46' }}>
                  ${fmtMoney(balanceDue)}
                </span>
              </div>
              <div className="form-group">
                <label className="form-label">Payment Status</label>
                <select className="form-select" value={paymentStatus}
                  onChange={e => setPaymentStatus(e.target.value)}>
                  <option value="pending">Pending</option>
                  <option value="deposit_paid">Deposit Paid</option>
                  <option value="paid_in_full">Paid in Full</option>
                  <option value="overdue">Overdue</option>
                </select>
              </div>
            </div>
          </div>

          {/* ─── Staffing Section ─────────────────────────────────────── */}
          <div className="card">
            <div className="card-header" style={{ padding: '12px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Users size={15} style={{ color: 'var(--color-text-muted)' }} />
                <span style={{ fontWeight: 700 }}>Crew</span>
                <span style={{
                  marginLeft: 4, fontSize: 'var(--text-xs)', fontWeight: 700,
                  padding: '2px 7px', borderRadius: 10,
                  background: staffAssignments.filter(a => a.status === 'confirmed').length >= parseInt(crewRequired)
                    ? '#DCFCE7' : '#FEF3C7',
                  color: staffAssignments.filter(a => a.status === 'confirmed').length >= parseInt(crewRequired)
                    ? '#15803D' : '#92400E',
                }}>
                  {staffAssignments.filter(a => a.status === 'confirmed').length} / {crewRequired} confirmed
                </span>
              </div>
              <button data-testid="crew-picker-open-button" className="btn btn-ghost btn-sm"
                onClick={() => setShowStaffPicker(true)}
                disabled={isNew}
                title={isNew ? 'Save job first' : 'Add crew member'}>
                <Plus size={14} /> Add
              </button>
            </div>

            {isNew && (
              <div style={{ padding: '12px 18px', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                Save the job first to manage crew.
              </div>
            )}

            {!isNew && (
              <div data-testid="assigned-crew-section">
                {staffMsg && (
                  <div style={{
                    margin: '10px 18px 0', padding: '8px 12px', borderRadius: 6,
                    background: staffMsg.startsWith('Error') || staffMsg.startsWith('Could') ? '#FEF2F2' : '#F0FDF4',
                    color: staffMsg.startsWith('Error') || staffMsg.startsWith('Could') ? '#B91C1C' : '#15803D',
                    fontSize: 'var(--text-xs)', fontWeight: 600,
                  }}>
                    {staffMsg}
                  </div>
                )}

                {staffAssignments.length === 0 ? (
                  <div style={{ padding: '20px 18px', textAlign: 'center' }}>
                    <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', marginBottom: 10 }}>
                      No crew assigned yet. {crewRequired} needed.
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={() => setShowStaffPicker(true)}>
                      <Plus size={14} /> Add First Crew Member
                    </button>
                  </div>
                ) : (
                  <div>
                    {staffAssignments.map(a => {
                      const statusMap = {
                        pinged:      { label: a.pinged_at ? 'Pinged' : 'Added', bg: '#F1F5F9', color: '#475569', border: '#CBD5E1' },
                        available:   { label: 'Available ✓', bg: '#ECFDF5', color: '#065F46', border: '#6EE7B7' },
                        unavailable: { label: 'Unavailable', bg: '#FEF2F2', color: '#B91C1C', border: '#FECACA' },
                        confirmed:   { label: 'Confirmed', bg: '#EFF6FF', color: '#1D4ED8', border: '#BFDBFE' },
                        declined:    { label: 'Declined', bg: '#FEF2F2', color: '#B91C1C', border: '#FECACA' },
                      }
                      const s = statusMap[a.status] || statusMap.pinged
                      const isBusy = pingingSend[a.id]

                      return (
                        <div
                          key={a.id}
                          data-testid={`assigned-staff-row-${a.user_id || a.id}`}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10,
                            padding: '10px 18px', borderBottom: '1px solid var(--color-border)',
                          }}
                        >
                          {/* Avatar */}
                          <div style={{
                            width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                            background: 'var(--color-navy)', color: 'white',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontWeight: 700, fontSize: 12,
                          }}>
                            {(a.users?.full_name || 'U').charAt(0).toUpperCase()}
                          </div>

                          {/* Name + status */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 'var(--text-sm)', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                              {a.users?.full_name || '—'}
                              <span style={{
                                fontSize: 'var(--text-xs)', padding: '1px 7px', borderRadius: 10,
                                background: s.bg, color: s.color, border: `1px solid ${s.border}`,
                                fontWeight: 600,
                              }}>
                                {s.label}
                              </span>
                            </div>
                            {a.users?.email && (
                              <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 1, display: 'flex', alignItems: 'center', gap: 4 }}>
                                <Mail size={10} /> {a.users.email}
                              </div>
                            )}
                          </div>

                          {/* Action buttons */}
                          <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                            {/* Send ping — show if not yet responded or to re-ping */}
                            {(a.status === 'pinged') && (
                              <button className="btn btn-ghost btn-sm" disabled={isBusy}
                                title={a.pinged_at ? 'Re-send ping' : 'Send availability ping'}
                                onClick={() => handleSendPing(a)}
                                style={{ fontSize: 11, padding: '3px 8px' }}>
                                {isBusy ? <Loader size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={12} />}
                                {a.pinged_at ? 'Re-ping' : 'Ping'}
                              </button>
                            )}

                            {/* Confirm — show if marked available */}
                            {a.status === 'available' && (
                              <button className="btn btn-sm" disabled={isBusy}
                                title="Confirm on crew + send confirmation email"
                                onClick={() => handleConfirmStaff(a)}
                                style={{
                                  fontSize: 11, padding: '3px 8px',
                                  background: 'var(--color-green)', color: 'white', border: 'none',
                                }}>
                                {isBusy ? <Loader size={12} /> : <UserCheck size={12} />}
                                Confirm
                              </button>
                            )}

                            {/* Remove */}
                            {a.status !== 'confirmed' && (
                              <button className="btn btn-ghost btn-sm" title="Remove from job"
                                onClick={() => handleRemoveStaff(a.id)}
                                style={{ color: 'var(--color-danger)', padding: '3px 6px' }}>
                                <X size={12} />
                              </button>
                            )}
                          </div>
                        </div>
                      )
                    })}

                    {/* "Ping all unpinged" shortcut */}
                    {staffAssignments.some(a => a.status === 'pinged' && !a.pinged_at) && (
                      <div style={{ padding: '10px 18px', borderTop: '1px solid var(--color-border)' }}>
                        <button className="btn btn-ghost btn-sm" onClick={handleSendAllPings}
                          style={{ fontSize: 'var(--text-xs)' }}>
                          <Send size={12} /> Ping all un-contacted crew
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Linked quote */}
          {linkedQuote && (
            <div className="card">
              <div className="card-header" style={{ padding: '14px 18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Briefcase size={15} style={{ color: 'var(--color-text-muted)' }} />
                  <span style={{ fontWeight: 700 }}>Linked Quote</span>
                </div>
              </div>
              <div style={{ padding: '14px 18px' }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 600, fontSize: 'var(--text-sm)', color: 'var(--color-navy)' }}>
                  {linkedQuote.quote_number}
                </div>
                {linkedQuote.grand_total && (
                  <div style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', marginTop: 4 }}>
                    ${fmtMoney(linkedQuote.grand_total)}
                  </div>
                )}
                <button className="btn btn-ghost btn-sm" style={{ marginTop: 8, padding: '4px 0' }}
                  onClick={() => navigate(`/quotes/${job?.quote_id}`)}>
                  View Quote →
                </button>
              </div>
            </div>
          )}

          {/* Next Step Panel */}
          {nextStep && (
            <div className="card">
              <div className="card-header" style={{ padding: '12px 18px' }}>
                <span style={{ fontWeight: 700, fontSize: 'var(--text-sm)' }}>Next Step</span>
              </div>
              <div className="card-body" style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div style={{
                  background: nextStep.bg,
                  border: `1px solid ${nextStep.border}`,
                  borderRadius: 'var(--radius-sm)',
                  padding: '10px 12px',
                  fontSize: 'var(--text-sm)',
                  color: nextStep.color,
                  lineHeight: 1.5,
                }}>
                  {nextStep.msg}
                </div>
                {nextStep.action === 'start' && (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => setStatus('in_progress')}
                    disabled={saving}
                    style={{ width: '100%', justifyContent: 'center' }}
                  >
                    Start Job
                  </button>
                )}
                {nextStep.action === 'complete' && (
                  <button
                    className="btn btn-success btn-sm"
                    onClick={() => setStatus('completed')}
                    disabled={saving}
                    style={{ width: '100%', justifyContent: 'center' }}
                  >
                    <CheckCircle size={13} /> Mark Complete
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Save button */}
          <button className="btn btn-primary" onClick={handleSave} disabled={saving} style={{ width: '100%' }}>
            <Save size={14} /> {saving ? 'Saving…' : 'Save Job'}
          </button>
        </div>
      </div>

      {/* Category picker modal */}
      {showCatPicker && (
        <CategoryPicker
          onSelect={cat => addInventoryItem(cat)}
          onClose={() => setShowCatPicker(false)}
          excludeIds={existingCatIds}
        />
      )}

      {showStaffPicker && (
        <StaffPicker
          onSelect={user => handleAddStaff(user)}
          onClose={() => setShowStaffPicker(false)}
          excludeIds={staffAssignments.map(a => a.user_id)}
        />
      )}
    </div>
  )
}
